import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Clock, Check, X, Volume2, VolumeX, PhoneCall, 
  RefreshCw, AlertTriangle, ChevronDown, LogOut,
  Bell, Utensils, Users, Calendar, Printer
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const KitchenDashboard = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [activeTab, setActiveTab] = useState('orders');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showDeclineModal, setShowDeclineModal] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [prepTime, setPrepTime] = useState({});
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const audioRef = useRef(null);
  const prevOrderCountRef = useRef(0);
  const prevBookingCountRef = useRef(0);

  // PWA Install Prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      // Show install banner if not already installed
      if (!window.matchMedia('(display-mode: standalone)').matches) {
        setShowInstallBanner(true);
      }
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setShowInstallBanner(false);
    }
    setInstallPrompt(null);
  };

  // Print receipt for thermal printer
  const printReceipt = (order) => {
    const items = order.line_items || order.items || [];
    const escText = (str) => {
      const div = document.createElement('div');
      div.textContent = str || '';
      return div.innerHTML;
    };
    const w = window.open('', '_blank', 'width=300,height=600');
    if (!w) return;
    const doc = w.document;
    doc.open();
    doc.write(`<!DOCTYPE html><html><head><style>
      @page { margin: 0; size: 80mm auto; }
      body { font-family: 'Courier New', monospace; font-size: 12px; width: 72mm; margin: 4mm; color: #000; }
      .center { text-align: center; }
      .bold { font-weight: bold; }
      .line { border-top: 1px dashed #000; margin: 4px 0; }
      .row { display: flex; justify-content: space-between; }
      h1 { font-size: 16px; margin: 2px 0; }
      h2 { font-size: 13px; margin: 2px 0; }
      .big { font-size: 18px; font-weight: bold; }
    </style></head><body>
      <div class="center">
        <h1 class="bold">MASALA INDISK</h1>
        <p>Restaurant og Bar</p>
        <p>Rikard Kaarbos plass 1, Harstad</p>
        <p>Tlf: +47 48 91 51 90</p>
      </div>
      <div class="line"></div>
      <div class="center big">BESTILLING #${escText(order.order_number)}</div>
      <div class="line"></div>
      <div class="row"><span class="bold">Kunde:</span><span>${escText(order.customer_name)}</span></div>
      <div class="row"><span class="bold">Tlf:</span><span>${escText(order.customer_phone || '-')}</span></div>
      ${order.pickup_time ? `<div class="row"><span class="bold">Henting:</span><span>${escText(order.pickup_time)}</span></div>` : ''}
      ${order.pickup_date ? `<div class="row"><span class="bold">Dato:</span><span>${escText(order.pickup_date)}</span></div>` : ''}
      <div class="line"></div>
      <h2>VARER:</h2>
      ${items.map(item => `<div class="row"><span>${escText(String(item.quantity))}x ${escText(item.name)}</span><span>kr ${escText(String(item.total_price || item.total))},-</span></div>`).join('')}
      <div class="line"></div>
      <div class="row bold"><span>TOTAL:</span><span>kr ${escText(String(order.total_amount))},-</span></div>
      ${order.tip_amount > 0 ? `<div class="row"><span>Tips:</span><span>kr ${escText(String(order.tip_amount))},-</span></div>` : ''}
      <div class="row"><span>Betaling:</span><span>${order.payment_method === 'at_restaurant' ? 'I restauranten' : 'Betalt online'}</span></div>
      ${order.special_instructions ? `<div class="line"></div><p class="bold">MERKNAD: ${escText(order.special_instructions)}</p>` : ''}
      <div class="line"></div>
      <div class="center"><p>${new Date().toLocaleString('no-NO')}</p></div>
      <script>window.onload=function(){window.print();setTimeout(function(){window.close()},500);}<\/script>
    </body></html>`);
    doc.close();
  };

  // Timer tick every second for countdown
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Audio setup
  useEffect(() => {
    audioRef.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdGKrdJB0gHJ+fYJ2dH57hYeLh4N8eH+DhIWCe3V3fYSJioiDfHl7gYaIhoN9eXt/hIiIhn95eHqBhYiHhH56eX6DhoiGg356en6BhIaFg316ent/goWGhIN8eXp9goWGhYN8eXp+goSGhIN8eXt+goSFhIN8ent+goSFhIN8eXt/goSFhIN8eXt/goSFg4N9ent/goSFg4J9ent/gYSFg4J9ent/gYSFg4J9ent/gYOEg4J9eXt/gYOEg4J9ent/gYOEg4J9ent/gYOEhIJ9enp/gYOEhIJ9enp/gYOEhIJ9enp/gYOEhIJ9');
  }, []);

  const playAlertSound = useCallback(() => {
    if (!soundEnabled) return;
    try {
      // Use Web Audio API for a louder ring-tone style alert
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const playTone = (freq, start, dur) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.4, ctx.currentTime + start);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + start + dur);
        osc.start(ctx.currentTime + start);
        osc.stop(ctx.currentTime + start + dur);
      };
      // Ring pattern: 3 rapid beeps
      for (let i = 0; i < 3; i++) {
        playTone(880, i * 0.3, 0.2);
        playTone(1100, i * 0.3 + 0.05, 0.15);
      }
      // Then 2 longer tones
      playTone(660, 1.0, 0.4);
      playTone(880, 1.5, 0.4);
    } catch (e) {
    }
  }, [soundEnabled]);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('kitchen_token') || localStorage.getItem('admin_token');
      if (!token) {
        const isKitchenRoute = window.location.pathname.startsWith('/kjokkenet');
        navigate(isKitchenRoute ? '/kjokkenet/login' : '/panel050623/login');
        return;
      }

      const [ordersRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/orders`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/bookings`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      const newOrders = ordersRes.data.filter(
        o => (o.status === 'paid' || o.status === 'pending') &&
          !(o.payment_method === 'online' && o.payment_status !== 'paid')
      );
      const newBookings = bookingsRes.data.filter(
        b => b.status === 'pending'
      );

      // Play sound if new orders/bookings arrived
      if (newOrders.length > prevOrderCountRef.current || newBookings.length > prevBookingCountRef.current) {
        playAlertSound();
      }
      prevOrderCountRef.current = newOrders.length;
      prevBookingCountRef.current = newBookings.length;

      setOrders(ordersRes.data);
      setBookings(bookingsRes.data);
      setLoading(false);
    } catch (err) {
      console.error('Fetch error:', err);
      if (err.response?.status === 401) navigate('/panel050623/login');
      setLoading(false);
    }
  }, [navigate, playAlertSound]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Accept order
  const handleAcceptOrder = async (orderNumber) => {
    const minutes = prepTime[orderNumber] || 20;
    try {
      const token = localStorage.getItem('kitchen_token') || localStorage.getItem('admin_token');
      await fetch(`${API}/kitchen/order-action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          order_number: orderNumber,
          status: 'Accepted',
          prep_time: parseInt(minutes)
        })
      });
      fetchData();
    } catch (err) {
      console.error('Accept error:', err);
    }
  };

  // Decline order
  const handleDeclineOrder = async (orderNumber) => {
    try {
      const token = localStorage.getItem('kitchen_token') || localStorage.getItem('admin_token');
      await fetch(`${API}/kitchen/order-action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          order_number: orderNumber,
          status: 'Declined',
          decline_reason: declineReason || undefined
        })
      });
      setShowDeclineModal(null);
      setDeclineReason('');
      fetchData();
    } catch (err) {
      console.error('Decline error:', err);
    }
  };

  // Accept/decline booking
  const handleBookingAction = async (bookingId, status) => {
    try {
      const token = localStorage.getItem('kitchen_token') || localStorage.getItem('admin_token');
      await axios.put(`${API}/bookings/${bookingId}/status`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      console.error('Booking action error:', err);
    }
  };

  // Get seconds since order was created
  const getSecondsElapsed = (createdAt) => {
    if (!createdAt) return 0;
    const created = new Date(createdAt).getTime();
    return Math.floor((now - created) / 1000);
  };

  // Get countdown progress (8 minutes = 480 seconds)
  const getCountdownProgress = (createdAt) => {
    const elapsed = getSecondsElapsed(createdAt);
    return Math.min(elapsed / 480, 1);
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // Filter orders by category
  const today = new Date().toISOString().split('T')[0];
  
  // Orders
  const pendingOrders = orders.filter(o => 
    (o.status === 'paid' || o.status === 'pending') &&
    !(o.payment_method === 'online' && o.payment_status !== 'paid')
  );
  const todayOrders = orders.filter(o => 
    (o.status === 'confirmed' || o.status === 'completed') && 
    (o.pickup_date === today || (!o.pickup_date && o.created_at && o.created_at.startsWith(today)))
  );
  const futureOrders = orders.filter(o => 
    (o.status === 'paid' || o.status === 'pending' || o.status === 'confirmed') && 
    o.pickup_date && o.pickup_date > today &&
    !(o.payment_method === 'online' && o.payment_status !== 'paid')
  );
  const completedOrders = orders.filter(o => o.status === 'declined' || o.status === 'completed');

  // Bookings
  const pendingBookings = bookings.filter(b => b.status === 'pending');
  const todayBookings = bookings.filter(b => 
    b.status === 'confirmed' && b.booking_date === today
  );
  const futureBookings = bookings.filter(b => 
    (b.status === 'pending' || b.status === 'confirmed') && 
    b.booking_date && b.booking_date > today
  );

  // Sort helper - by scheduled_time or created_at ASC
  const sortByTime = (a, b) => {
    const timeA = a.scheduled_time || (a.created_at ? a.created_at.slice(11, 16) : '99:99');
    const timeB = b.scheduled_time || (b.created_at ? b.created_at.slice(11, 16) : '99:99');
    return timeA.localeCompare(timeB);
  };

  // Sort helper - by date then time ASC
  const sortByDateTime = (a, b) => {
    const dateA = a.pickup_date || a.booking_date || '9999-99-99';
    const dateB = b.pickup_date || b.booking_date || '9999-99-99';
    if (dateA !== dateB) return dateA.localeCompare(dateB);
    return sortByTime(a, b);
  };

  const pendingCount = pendingOrders.length + pendingBookings.length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#1a1a2e' }}>
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-yellow-400 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-yellow-400 text-lg font-semibold">Laster kjokken...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#0f0f1a' }}>
      {/* PWA Install Banner */}
      {showInstallBanner && (
        <div className="px-4 py-3 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #d4af37, #b8960c)' }}>
          <div className="flex items-center gap-3">
            <img src="/images/icon-96x96.png" alt="" className="w-10 h-10 rounded-xl" />
            <div>
              <p className="text-sm font-bold" style={{ color: '#2d1810' }}>Installer Masala Kjokken</p>
              <p className="text-xs" style={{ color: '#4a3728' }}>Legg til pa hjemskjermen for enklere tilgang</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowInstallBanner(false)}
              className="text-xs px-3 py-1.5 rounded-full font-semibold"
              style={{ color: '#4a3728' }}
            >
              Senere
            </button>
            <button
              onClick={handleInstall}
              className="text-xs px-4 py-1.5 rounded-full font-bold text-white"
              style={{ background: '#721111' }}
              data-testid="pwa-install-btn"
            >
              Installer
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="sticky top-0 z-50 px-4 py-3" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', borderBottom: '2px solid #d4af37' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-yellow-400">Masala Kjokken</h1>
            {pendingCount > 0 && (
              <span className="relative flex h-8 w-8 items-center justify-center">
                <span className="animate-ping absolute h-full w-full rounded-full bg-red-500 opacity-75"></span>
                <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-red-600 text-white text-sm font-bold">
                  {pendingCount}
                </span>
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="p-2 rounded-lg transition-colors"
              style={{ background: soundEnabled ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)' }}
              data-testid="sound-toggle"
            >
              {soundEnabled ? <Volume2 className="w-5 h-5 text-green-400" /> : <VolumeX className="w-5 h-5 text-red-400" />}
            </button>
            <button onClick={fetchData} className="p-2 rounded-lg" style={{ background: 'rgba(212,175,55,0.2)' }}>
              <RefreshCw className="w-5 h-5 text-yellow-400" />
            </button>
            <button
              onClick={() => {
                localStorage.removeItem('kitchen_token');
                localStorage.removeItem('admin_token');
                const isKitchenRoute = window.location.pathname.startsWith('/kjokkenet');
                navigate(isKitchenRoute ? '/kjokkenet/login' : '/panel050623/login');
              }}
              className="p-2 rounded-lg" style={{ background: 'rgba(239,68,68,0.2)' }}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 text-red-400" />
            </button>
          </div>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="px-4 py-2" style={{ background: '#16213e' }}>
        <div className="max-w-7xl mx-auto flex gap-2">
          <button
            onClick={() => setActiveTab('orders')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
              activeTab === 'orders' ? 'bg-yellow-500 text-black' : 'text-gray-400 hover:text-white'
            }`}
            data-testid="tab-orders"
          >
            <Utensils className="w-4 h-4" />
            Bestillinger
            {pendingOrders.length > 0 && (
              <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded-full">{pendingOrders.length}</span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('bookings')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
              activeTab === 'bookings' ? 'bg-yellow-500 text-black' : 'text-gray-400 hover:text-white'
            }`}
            data-testid="tab-bookings"
          >
            <Users className="w-4 h-4" />
            Reservasjoner
            {pendingBookings.length > 0 && (
              <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded-full">{pendingBookings.length}</span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
              activeTab === 'history' ? 'bg-yellow-500 text-black' : 'text-gray-400 hover:text-white'
            }`}
            data-testid="tab-history"
          >
            <Clock className="w-4 h-4" />
            Historikk
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-4">

        {/* ORDERS TAB */}
        {activeTab === 'orders' && (
          <div>
            {pendingOrders.length === 0 && todayOrders.length === 0 && futureOrders.length === 0 ? (
              <div className="text-center py-20">
                <Bell className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500 text-xl font-semibold">Ingen bestillinger</p>
                <p className="text-gray-600 text-sm mt-2">Venter pa bestillinger...</p>
              </div>
            ) : (
              <>
                {/* 1. NYE BESTILLINGER - Pending */}
                {pendingOrders.length > 0 && (
                  <div className="mb-8">
                    <h2 className="text-lg font-bold text-red-400 mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      NYE BESTILLINGER ({pendingOrders.length})
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                      {[...pendingOrders].sort(sortByTime).map(order => (
                        <OrderCard
                          key={order.order_number + '_pending'}
                          order={order}
                          now={now}
                          getSecondsElapsed={getSecondsElapsed}
                          getCountdownProgress={getCountdownProgress}
                          formatTime={formatTime}
                          prepTime={prepTime}
                          setPrepTime={setPrepTime}
                          onAccept={handleAcceptOrder}
                          onDecline={() => setShowDeclineModal(order.order_number)}
                          onPrint={printReceipt}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* 2. DAGENS BESTILLINGER - Confirmed today */}
                {todayOrders.length > 0 && (
                  <div className="mb-8">
                    <h2 className="text-lg font-bold text-green-400 mb-3 flex items-center gap-2">
                      <Check className="w-5 h-5" />
                      DAGENS BESTILLINGER ({todayOrders.length})
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                      {[...todayOrders].sort(sortByTime).map(order => (
                        <div key={order.order_number + '_today'} className="rounded-xl p-4 border" style={{ background: '#1a2744', borderColor: '#22c55e40' }}>
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-green-400 font-bold">#{order.order_number}</span>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              order.status === 'completed' ? 'bg-blue-900 text-blue-300' : 'bg-green-600 text-white'
                            }`}>
                              {order.status === 'completed' ? 'Fullfort' : `Klar om ${order.estimated_minutes || '?'} min`}
                            </span>
                          </div>
                          <p className="text-white font-semibold">{order.customer_name}</p>
                          <p className="text-gray-400 text-sm">{order.customer_phone}</p>
                          <div className="mt-1 space-y-0.5">
                            {order.created_at && (
                              <div className="flex items-center gap-1">
                                <Clock className="w-3 h-3 text-gray-500" />
                                <span className="text-gray-500 text-xs">Bestilt: kl. {new Date(order.created_at).toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}</span>
                              </div>
                            )}
                            {order.pickup_type === 'scheduled' && order.scheduled_time ? (
                              <div className="flex items-center gap-1">
                                <Clock className="w-3 h-3 text-blue-400" />
                                <span className="text-blue-400 text-xs font-semibold">Henting: {order.pickup_time || `kl. ${order.scheduled_time}`}</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1">
                                <Clock className="w-3 h-3 text-yellow-400" />
                                <span className="text-yellow-400 text-xs font-semibold">Sa fort som mulig</span>
                              </div>
                            )}
                          </div>
                          {order.customer_notes && (
                            <p className="text-yellow-300 text-xs mt-1 italic bg-yellow-900/30 px-2 py-1 rounded">
                              "{order.customer_notes}"
                            </p>
                          )}
                          <div className="mt-2 space-y-1 bg-black/20 rounded-lg p-2">
                            {(order.line_items || order.items || []).map((item, i) => (
                              <div key={i} className="text-sm text-gray-300 flex justify-between">
                                <span>{item.quantity}x {item.name}</span>
                                <span>kr {item.total_price || item.total},-</span>
                              </div>
                            ))}
                          </div>
                          <div className="flex justify-between font-bold mt-2 pt-2 border-t border-gray-700">
                            <span className="text-white">TOTAL:</span>
                            <span className="text-green-400">kr {order.total_amount},-</span>
                          </div>
                          {order.tip_amount > 0 && (
                            <div className="flex justify-between text-sm mt-1">
                              <span className="text-yellow-300">Inkl. tips:</span>
                              <span className="text-yellow-300">kr {order.tip_amount},-</span>
                            </div>
                          )}
                          <div className="flex flex-wrap gap-2 mt-2">
                            {order.payment_method === 'at_restaurant' ? (
                              <span className="bg-orange-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                                Betaler i restauranten
                              </span>
                            ) : (
                              <span className="bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                                Betalt online
                              </span>
                            )}
                            {order.marketing_consent ? (
                              <span className="bg-green-700 text-green-100 text-xs font-bold px-3 py-1 rounded-full">E-post: Ja</span>
                            ) : (
                              <span className="bg-gray-700 text-gray-400 text-xs px-3 py-1 rounded-full">E-post: Nei</span>
                            )}
                            <button
                              onClick={() => printReceipt(order)}
                              className="bg-gray-600 hover:bg-gray-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1"
                              data-testid={`print-today-${order.order_number}`}
                            >
                              <Printer className="w-3 h-3" /> Skriv ut
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 3. FREMTIDIGE BESTILLINGER - Future dates */}
                {futureOrders.length > 0 && (
                  <div className="mb-8">
                    <h2 className="text-lg font-bold text-blue-400 mb-3 flex items-center gap-2">
                      <Calendar className="w-5 h-5" />
                      FREMTIDIGE BESTILLINGER ({futureOrders.length})
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                      {[...futureOrders].sort(sortByDateTime).map(order => (
                        <div key={order.order_number + '_future'} className="rounded-xl p-4 border" style={{ background: '#1a2744', borderColor: '#3b82f640' }}>
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-blue-400 font-bold">#{order.order_number}</span>
                            <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
                              {order.pickup_date}
                            </span>
                          </div>
                          <p className="text-white font-semibold">{order.customer_name}</p>
                          <p className="text-gray-400 text-sm">{order.customer_phone}</p>
                          {order.pickup_time && (
                            <div className="flex items-center gap-1 mt-1">
                              <Clock className="w-4 h-4 text-blue-400" />
                              <span className="text-blue-400 text-sm font-semibold">Henting: {order.pickup_time}</span>
                            </div>
                          )}
                          <div className="mt-2 space-y-1 bg-black/20 rounded-lg p-2">
                            {(order.line_items || order.items || []).map((item, i) => (
                              <div key={i} className="text-sm text-gray-300 flex justify-between">
                                <span>{item.quantity}x {item.name}</span>
                                <span>kr {item.total_price || item.total},-</span>
                              </div>
                            ))}
                          </div>
                          <div className="flex justify-between font-bold mt-2 pt-2 border-t border-gray-700">
                            <span className="text-white">TOTAL:</span>
                            <span className="text-blue-400">kr {order.total_amount},-</span>
                          </div>
                          <span className={`mt-2 inline-block text-xs px-2 py-1 rounded-full ${
                            order.status === 'confirmed' ? 'bg-green-900 text-green-300' : 'bg-yellow-900 text-yellow-300'
                          }`}>
                            {order.status === 'confirmed' ? 'Godkjent' : 'Venter'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* BOOKINGS TAB */}
        {activeTab === 'bookings' && (
          <div>
            {pendingBookings.length === 0 && todayBookings.length === 0 && futureBookings.length === 0 ? (
              <div className="text-center py-20">
                <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500 text-xl font-semibold">Ingen reservasjoner</p>
              </div>
            ) : (
              <>
                {/* 1. NYE RESERVASJONER - Pending */}
                {pendingBookings.length > 0 && (
                  <div className="mb-8">
                    <h2 className="text-lg font-bold text-red-400 mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      NYE RESERVASJONER ({pendingBookings.length})
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                      {[...pendingBookings].sort(sortByDateTime).map(booking => (
                        <div key={booking.id || booking.booking_id} className="rounded-xl p-4 border" style={{ background: '#1a2744', borderColor: '#d4af3740' }}>
                          <div className="flex justify-between items-start mb-3">
                            <span className="text-yellow-400 font-bold text-lg">{booking.customer_name}</span>
                            <span className="bg-yellow-600 text-white text-xs px-2 py-1 rounded-full">Ny</span>
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center gap-2 text-gray-300">
                              <Calendar className="w-4 h-4 text-yellow-400" />
                              {booking.booking_date} kl. {booking.booking_time}
                            </div>
                            <div className="flex items-center gap-2 text-gray-300">
                              <Users className="w-4 h-4 text-yellow-400" />
                              {booking.number_of_guests} gjester
                            </div>
                            <div className="flex items-center gap-2 text-gray-300">
                              <PhoneCall className="w-4 h-4 text-yellow-400" />
                              {booking.customer_phone}
                            </div>
                            {booking.special_requests && (
                              <p className="text-gray-400 italic text-xs mt-1">"{booking.special_requests}"</p>
                            )}
                            {booking.marketing_consent ? (
                              <span className="inline-block mt-1 bg-green-700 text-green-100 text-xs font-bold px-2 py-0.5 rounded-full">E-post: Ja</span>
                            ) : (
                              <span className="inline-block mt-1 bg-gray-700 text-gray-400 text-xs px-2 py-0.5 rounded-full">E-post: Nei</span>
                            )}
                          </div>
                          <div className="flex gap-2 mt-4">
                            <Button
                              onClick={() => handleBookingAction(booking.id || booking.booking_id, 'confirmed')}
                              className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold"
                              data-testid={`accept-booking-${booking.id || booking.booking_id}`}
                            >
                              <Check className="w-4 h-4 mr-1" /> Godkjenn
                            </Button>
                            <Button
                              onClick={() => handleBookingAction(booking.id || booking.booking_id, 'declined')}
                              className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold"
                              data-testid={`decline-booking-${booking.id || booking.booking_id}`}
                            >
                              <X className="w-4 h-4 mr-1" /> Avvis
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 2. DAGENS RESERVASJONER - Confirmed today */}
                {todayBookings.length > 0 && (
                  <div className="mb-8">
                    <h2 className="text-lg font-bold text-green-400 mb-3 flex items-center gap-2">
                      <Check className="w-5 h-5" />
                      DAGENS RESERVASJONER ({todayBookings.length})
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                      {[...todayBookings].sort(sortByTime).map(booking => (
                        <div key={booking.id || booking.booking_id} className="rounded-xl p-4 border" style={{ background: '#1a2744', borderColor: '#22c55e40' }}>
                          <div className="flex justify-between items-start mb-3">
                            <span className="text-green-400 font-bold text-lg">{booking.customer_name}</span>
                            <span className="bg-green-600 text-white text-xs px-2 py-1 rounded-full">Bekreftet</span>
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center gap-2 text-gray-300">
                              <Clock className="w-4 h-4 text-green-400" />
                              Kl. {booking.booking_time}
                            </div>
                            <div className="flex items-center gap-2 text-gray-300">
                              <Users className="w-4 h-4 text-green-400" />
                              {booking.number_of_guests} gjester
                            </div>
                            <div className="flex items-center gap-2 text-gray-300">
                              <PhoneCall className="w-4 h-4 text-green-400" />
                              {booking.customer_phone}
                            </div>
                            {booking.special_requests && (
                              <p className="text-gray-400 italic text-xs mt-1">"{booking.special_requests}"</p>
                            )}
                            {booking.marketing_consent ? (
                              <span className="inline-block mt-1 bg-green-700 text-green-100 text-xs font-bold px-2 py-0.5 rounded-full">E-post: Ja</span>
                            ) : (
                              <span className="inline-block mt-1 bg-gray-700 text-gray-400 text-xs px-2 py-0.5 rounded-full">E-post: Nei</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 3. FREMTIDIGE RESERVASJONER - Future dates */}
                {futureBookings.length > 0 && (
                  <div className="mb-8">
                    <h2 className="text-lg font-bold text-blue-400 mb-3 flex items-center gap-2">
                      <Calendar className="w-5 h-5" />
                      FREMTIDIGE RESERVASJONER ({futureBookings.length})
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                      {[...futureBookings].sort(sortByDateTime).map(booking => (
                        <div key={booking.id || booking.booking_id} className="rounded-xl p-4 border" style={{ background: '#1a2744', borderColor: '#3b82f640' }}>
                          <div className="flex justify-between items-start mb-3">
                            <span className="text-blue-400 font-bold text-lg">{booking.customer_name}</span>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              booking.status === 'confirmed' ? 'bg-green-600 text-white' : 'bg-yellow-600 text-white'
                            }`}>
                              {booking.status === 'confirmed' ? 'Bekreftet' : 'Venter'}
                            </span>
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center gap-2 text-gray-300">
                              <Calendar className="w-4 h-4 text-blue-400" />
                              {booking.booking_date} kl. {booking.booking_time}
                            </div>
                            <div className="flex items-center gap-2 text-gray-300">
                              <Users className="w-4 h-4 text-blue-400" />
                              {booking.number_of_guests} gjester
                            </div>
                            <div className="flex items-center gap-2 text-gray-300">
                              <PhoneCall className="w-4 h-4 text-blue-400" />
                              {booking.customer_phone}
                            </div>
                            {booking.special_requests && (
                              <p className="text-gray-400 italic text-xs mt-1">"{booking.special_requests}"</p>
                            )}
                            {booking.marketing_consent ? (
                              <span className="inline-block mt-1 bg-green-700 text-green-100 text-xs font-bold px-2 py-0.5 rounded-full">E-post: Ja</span>
                            ) : (
                              <span className="inline-block mt-1 bg-gray-700 text-gray-400 text-xs px-2 py-0.5 rounded-full">E-post: Nei</span>
                            )}
                          </div>
                          {booking.status === 'pending' && (
                            <div className="flex gap-2 mt-4">
                              <Button
                                onClick={() => handleBookingAction(booking.id || booking.booking_id, 'confirmed')}
                                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold"
                              >
                                <Check className="w-4 h-4 mr-1" /> Godkjenn
                              </Button>
                              <Button
                                onClick={() => handleBookingAction(booking.id || booking.booking_id, 'declined')}
                                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold"
                              >
                                <X className="w-4 h-4 mr-1" /> Avvis
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* HISTORY TAB */}
        {activeTab === 'history' && (
          <div>
            {completedOrders.length === 0 ? (
              <div className="text-center py-20">
                <Clock className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500 text-xl font-semibold">Ingen historikk enna</p>
              </div>
            ) : (
              <div className="space-y-3">
                {completedOrders.slice(0, 20).map(order => (
                  <div key={order.order_number} className="rounded-xl p-4 flex items-center justify-between" style={{ background: '#1a2744' }}>
                    <div>
                      <span className="text-gray-400 text-sm">#{order.order_number}</span>
                      <p className="text-white font-semibold">{order.customer_name}</p>
                    </div>
                    <div className="text-right">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        order.status === 'declined' ? 'bg-red-900 text-red-300' : 'bg-green-900 text-green-300'
                      }`}>
                        {order.status === 'declined' ? 'Avvist' : 'Fullfort'}
                      </span>
                      <p className="text-yellow-400 font-bold mt-1">kr {order.total_amount},-</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Decline Modal */}
      {showDeclineModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.8)' }}>
          <div className="w-full max-w-md rounded-2xl p-6" style={{ background: '#1a2744', border: '2px solid #dc2626' }}>
            <h3 className="text-xl font-bold text-red-400 mb-4">Avvis bestilling #{showDeclineModal}</h3>
            <p className="text-gray-400 text-sm mb-3">Oppgi grunn (valgfritt - sendes til kunden):</p>
            <div className="space-y-2 mb-4">
              {['Kjoekkenet er fullt akkurat naa', 'Mangler ingredienser', 'Vi stenger snart', 'Annen grunn'].map((reason) => (
                <button
                  key={reason}
                  onClick={() => setDeclineReason(reason)}
                  className={`w-full text-left px-4 py-2 rounded-lg text-sm transition-all ${
                    declineReason === reason
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                  data-testid={`decline-reason-${reason.slice(0,10)}`}
                >
                  {reason}
                </button>
              ))}
            </div>
            {declineReason === 'Annen grunn' && (
              <Input
                placeholder="Skriv din grunn..."
                value={declineReason === 'Annen grunn' ? '' : declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                className="mb-4 bg-gray-800 text-white border-gray-600"
                data-testid="decline-reason-custom"
              />
            )}
            <div className="flex gap-3">
              <Button
                onClick={() => { setShowDeclineModal(null); setDeclineReason(''); }}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white"
                data-testid="decline-cancel-btn"
              >
                Avbryt
              </Button>
              <Button
                onClick={() => handleDeclineOrder(showDeclineModal)}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold"
                data-testid="decline-confirm-btn"
              >
                Avvis bestilling
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Separate OrderCard component for pending orders
const OrderCard = ({ order, now, getSecondsElapsed, getCountdownProgress, formatTime, prepTime, setPrepTime, onAccept, onDecline, onPrint }) => {
  const elapsed = getSecondsElapsed(order.created_at);
  const progress = getCountdownProgress(order.created_at);
  const isUrgent = elapsed >= 480;
  const isWarning = elapsed >= 390 && elapsed < 480;

  const borderColor = isUrgent ? '#ef4444' : isWarning ? '#f59e0b' : '#d4af37';
  const bgGlow = isUrgent ? 'rgba(239,68,68,0.1)' : isWarning ? 'rgba(245,158,11,0.05)' : 'transparent';

  return (
    <div
      className={`rounded-xl p-4 border-2 transition-all ${isUrgent ? 'animate-pulse' : ''}`}
      style={{ background: `linear-gradient(135deg, #1a2744, ${bgGlow})`, borderColor }}
      data-testid={`order-card-${order.order_number}`}
    >
      {/* Countdown Bar */}
      <div className="w-full h-1.5 rounded-full mb-3 overflow-hidden" style={{ background: '#333' }}>
        <div
          className="h-full rounded-full transition-all duration-1000"
          style={{
            width: `${Math.min(progress * 100, 100)}%`,
            background: isUrgent ? '#ef4444' : isWarning ? '#f59e0b' : '#22c55e'
          }}
        />
      </div>

      {/* Header */}
      <div className="flex justify-between items-start mb-2">
        <div>
          <span className="text-yellow-400 font-bold text-lg" data-testid={`order-number-${order.order_number}`}>
            #{order.order_number}
          </span>
          {isUrgent && (
            <span className="ml-2 bg-red-600 text-white text-xs px-2 py-0.5 rounded-full animate-bounce">
              HASTER!
            </span>
          )}
        </div>
        <div className="text-right">
          <span className={`text-sm font-mono font-bold ${isUrgent ? 'text-red-400' : isWarning ? 'text-yellow-400' : 'text-green-400'}`}>
            {formatTime(elapsed)}
          </span>
          {order.escalation_sent && (
            <div className="flex items-center gap-1 mt-1">
              <PhoneCall className="w-3 h-3 text-orange-400" />
              <span className="text-orange-400 text-xs">Eskalert</span>
            </div>
          )}
        </div>
      </div>

      {/* Customer Info */}
      <p className="text-white font-semibold text-lg">{order.customer_name}</p>
      <p className="text-gray-400 text-sm">{order.customer_phone}</p>
      {order.customer_notes && (
        <p className="text-yellow-300 text-xs mt-1 italic bg-yellow-900/30 px-2 py-1 rounded">
          "{order.customer_notes}"
        </p>
      )}

      {/* Order time + Pickup time */}
      <div className="mt-2 space-y-1">
        {order.created_at && (
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4 text-gray-400" />
            <span className="text-gray-400 text-sm">Bestilt: kl. {new Date(order.created_at).toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
        )}
        {order.pickup_type === 'scheduled' && order.scheduled_time ? (
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4 text-blue-400" />
            <span className="text-blue-400 text-sm font-semibold">Henting: {order.pickup_time || `kl. ${order.scheduled_time}`}</span>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4 text-yellow-400" />
            <span className="text-yellow-400 text-sm font-semibold">Sa fort som mulig</span>
          </div>
        )}
      </div>

      {/* Items */}
      <div className="mt-3 space-y-1 bg-black/20 rounded-lg p-3">
        {(order.line_items || []).map((item, i) => (
          <div key={i} className="flex justify-between text-sm">
            <span className="text-gray-200">
              {item.quantity}x {item.name}
              {item.meat_choice && <span className="text-yellow-400"> ({item.meat_choice})</span>}
            </span>
            <span className="text-gray-400">kr {item.total_price},-</span>
          </div>
        ))}
      </div>

      {/* Total + Payment + Tip */}
      <div className="mt-3 pt-2 border-t border-gray-700">
        <div className="flex justify-between font-bold text-lg">
          <span className="text-white">TOTAL:</span>
          <span className="text-green-400">kr {order.total_amount},-</span>
        </div>
        {order.tip_amount > 0 && (
          <div className="flex justify-between text-sm mt-1">
            <span className="text-yellow-300">Inkl. tips:</span>
            <span className="text-yellow-300">kr {order.tip_amount},-</span>
          </div>
        )}
        <div className="flex flex-wrap gap-2 mt-2">
          {order.payment_method === 'at_restaurant' ? (
            <span className="bg-orange-600 text-white text-xs font-bold px-3 py-1 rounded-full" data-testid={`payment-badge-${order.order_number}`}>
              Betaler i restauranten
            </span>
          ) : (
            <span className="bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full" data-testid={`payment-badge-${order.order_number}`}>
              Betalt online
            </span>
          )}
          {order.marketing_consent ? (
            <span className="bg-green-700 text-green-100 text-xs font-bold px-3 py-1 rounded-full">E-post: Ja</span>
          ) : (
            <span className="bg-gray-700 text-gray-400 text-xs px-3 py-1 rounded-full">E-post: Nei</span>
          )}
        </div>
      </div>

      {/* Prep Time + Actions */}
      <div className="mt-4 space-y-3">
        {/* Show prep time selector for ASAP orders (not scheduled ones) */}
        {order.pickup_type !== 'scheduled' && (
          <div>
            <label className="text-gray-400 text-xs block mb-1">Tilberedningstid (min)</label>
            <div className="flex gap-2 items-center">
              {[15, 20, 30, 45].map(t => (
                <button
                  key={t}
                  onClick={() => setPrepTime(prev => ({ ...prev, [order.order_number]: t }))}
                  className={`px-3 py-1.5 rounded-lg text-sm font-bold transition-all ${
                    (prepTime[order.order_number] || 20) === t
                      ? 'bg-yellow-500 text-black'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                  data-testid={`prep-time-${t}-${order.order_number}`}
                >
                  {t}
                </button>
              ))}
              <input
                type="number"
                min="5"
                max="120"
                placeholder="Annet"
                value={
                  [15, 20, 30, 45].includes(prepTime[order.order_number]) 
                    ? '' 
                    : (prepTime[order.order_number] || '')
                }
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (val > 0) setPrepTime(prev => ({ ...prev, [order.order_number]: val }));
                }}
                className="w-16 px-2 py-1.5 rounded-lg text-sm font-bold text-center bg-gray-800 text-white border border-gray-600 focus:border-yellow-500 focus:outline-none"
                data-testid={`prep-time-custom-${order.order_number}`}
              />
              <span className="text-gray-500 text-xs">min</span>
            </div>
          </div>
        )}
        <div className="flex gap-2">
          <Button
            onClick={() => onAccept(order.order_number)}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-3 text-base"
            data-testid={`accept-order-${order.order_number}`}
          >
            <Check className="w-5 h-5 mr-1" /> GODKJENN
          </Button>
          <Button
            onClick={onDecline}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-3 text-base"
            data-testid={`decline-order-${order.order_number}`}
          >
            <X className="w-5 h-5 mr-1" /> AVVIS
          </Button>
        </div>
        <Button
          onClick={() => onPrint(order)}
          className="w-full bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 text-sm mt-2"
          data-testid={`print-order-${order.order_number}`}
        >
          <Printer className="w-4 h-4 mr-1" /> SKRIV UT
        </Button>
      </div>
    </div>
  );
};

export default KitchenDashboard;
